// AssetRequirementForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Plus, X, AlertCircle } from 'lucide-react';
import { assetService } from '@services/assetService';
import { companyService } from '@services/companyService';
import CustomSelect from '../shared/CustomSelect';

const AssetRequirementForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        assetType: '',
        applicableClientType: '',
        mandatory: false,
        minimumPhotos: 1,
        maximumPhotos: 10,
        companyId: '',
        active: true
    });

    const [companies, setCompanies] = useState([]);
    const [attributes, setAttributes] = useState([]);
    const [newAttribute, setNewAttribute] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Predefined attribute templates for each asset type
    const attributeTemplates = {
        VEHICLE: [
            'chassis',
            'licensePlate',
            'make',
            'model',
            'yearOfManufacture',
            'color',
            'engineNumber',
            'registrationDate'
        ],
        LAND: [
            'titleNumber',
            'plotNumber',
            'county',
            'subCounty',
            'ward',
            'acreage',
            'landUse'
        ],
        PROPERTY: [
            'propertyType',
            'titleNumber',
            'address',
            'floor',
            'unitNumber',
            'bedrooms',
            'bathrooms',
            'squareMeters'
        ],
        PHONE: [
            'brand',
            'model',
            'imei',
            'serialNumber',
            'purchaseDate',
            'purchasePrice',
            'condition'
        ],
        ELECTRONICS: [
            'brand',
            'model',
            'serialNumber',
            'purchaseDate',
            'warranty',
            'condition'
        ],
        MACHINERY: [
            'manufacturer',
            'model',
            'serialNumber',
            'yearOfManufacture',
            'condition',
            'lastServiceDate'
        ],
        OTHER: []
    };

    useEffect(() => {
        fetchCompanies();
        if (isEditMode) {
            fetchRequirementDetails();
        }
    }, []);

    const fetchCompanies = async () => {
        try {
            const response = await companyService.getAll();
            if (response.success && response.data) {
                setCompanies(response.data);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const fetchRequirementDetails = async () => {
        try {
            setLoading(true);
            const response = await assetService.assetRequirements.getById(id);
            if (response.success && response.data) {
                const req = response.data;
                setFormData({
                    name: req.name,
                    description: req.description || '',
                    assetType: req.assetType,
                    applicableClientType: req.applicableClientType,
                    mandatory: req.mandatory,
                    minimumPhotos: req.minimumPhotos,
                    maximumPhotos: req.maximumPhotos,
                    companyId: req.companyId,
                    active: req.active
                });

                // Parse existing attributes
                if (req.requiredAttributes) {
                    try {
                        const parsed = JSON.parse(req.requiredAttributes);
                        setAttributes(Array.isArray(parsed) ? parsed : []);
                    } catch (e) {
                        setAttributes([]);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching requirement:', error);
            setError('Failed to load requirement details');
        } finally {
            setLoading(false);
        }
    };

    const handleAssetTypeChange = (value) => {
        setFormData(prev => ({ ...prev, assetType: value }));

        // Load template attributes for the selected asset type
        if (attributeTemplates[value] && attributes.length === 0) {
            setAttributes(attributeTemplates[value]);
        }
    };

    const handleLoadTemplate = () => {
        if (formData.assetType && attributeTemplates[formData.assetType]) {
            setAttributes(attributeTemplates[formData.assetType]);
        }
    };

    const handleAddAttribute = () => {
        if (newAttribute.trim() && !attributes.includes(newAttribute.trim())) {
            setAttributes([...attributes, newAttribute.trim()]);
            setNewAttribute('');
        }
    };

    const handleRemoveAttribute = (index) => {
        setAttributes(attributes.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');

        try {
            const payload = {
                ...formData,
                requiredAttributes: JSON.stringify(attributes)
            };

            let response;
            if (isEditMode) {
                response = await assetService.assetRequirements.update(id, payload);
            } else {
                response = await assetService.assetRequirements.create(payload);
            }

            if (response.success) {
                navigate('/compliance/assets/requirements');
            } else {
                setError(response.message || 'Failed to save requirement');
            }
        } catch (error) {
            console.error('Error saving requirement:', error);
            setError(error.message || 'Failed to save requirement');
        } finally {
            setSaving(false);
        }
    };

    const companyOptions = companies.map(company => ({
        value: company.id,
        label: company.name
    }));

    const assetTypeOptions = [
        { value: 'VEHICLE', label: 'Vehicle' },
        { value: 'LAND', label: 'Land' },
        { value: 'PROPERTY', label: 'Property' },
        { value: 'PHONE', label: 'Phone' },
        { value: 'ELECTRONICS', label: 'Electronics' },
        { value: 'MACHINERY', label: 'Machinery' },
        { value: 'OTHER', label: 'Other' }
    ];

    const clientTypeOptions = [
        { value: 'RETAIL', label: 'Retail' },
        { value: 'CORPORATE', label: 'Corporate' }
    ];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/compliance/assets/requirements')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h2 className="text-2xl font-semibold text-gray-900">
                        {isEditMode ? 'Edit Asset Requirement' : 'Add Asset Requirement'}
                    </h2>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="space-y-6">
                    {/* Basic Information Section */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Requirement Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., Vehicle Registration"
                                    required
                                />
                            </div>

                            {/* Company */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Company <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect
                                    value={formData.companyId}
                                    onChange={(value) => setFormData(prev => ({ ...prev, companyId: value }))}
                                    options={companyOptions}
                                    placeholder="Select a company"
                                    searchable
                                    error={!formData.companyId}
                                />
                            </div>

                            {/* Asset Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Asset Type <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect
                                    value={formData.assetType}
                                    onChange={handleAssetTypeChange}
                                    options={assetTypeOptions}
                                    placeholder="Select asset type"
                                    error={!formData.assetType}
                                />
                            </div>

                            {/* Client Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Applicable Client Type <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect
                                    value={formData.applicableClientType}
                                    onChange={(value) => setFormData(prev => ({ ...prev, applicableClientType: value }))}
                                    options={clientTypeOptions}
                                    placeholder="Select client type"
                                    error={!formData.applicableClientType}
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                rows="3"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Describe this asset requirement..."
                            />
                        </div>
                    </div>

                    {/* Settings Section */}
                    <div className="border-t pt-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Settings</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Minimum Photos */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Minimum Photos <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="50"
                                    value={formData.minimumPhotos}
                                    onChange={(e) => setFormData(prev => ({ ...prev, minimumPhotos: parseInt(e.target.value) }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            {/* Maximum Photos */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Maximum Photos <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="50"
                                    value={formData.maximumPhotos}
                                    onChange={(e) => setFormData(prev => ({ ...prev, maximumPhotos: parseInt(e.target.value) }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            {/* Mandatory Checkbox */}
                            <div className="flex items-center pt-7">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.mandatory}
                                        onChange={(e) => setFormData(prev => ({ ...prev, mandatory: e.target.checked }))}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Mandatory</span>
                                </label>
                            </div>
                        </div>

                        {/* Active Status */}
                        <div className="mt-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.active}
                                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700">Active</span>
                            </label>
                        </div>
                    </div>

                    {/* Required Attributes Section */}
                    <div className="border-t pt-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900">Required Attributes</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    Define the fields that will be collected for this asset type
                                </p>
                            </div>
                            {formData.assetType && attributeTemplates[formData.assetType].length > 0 && (
                                <button
                                    type="button"
                                    onClick={handleLoadTemplate}
                                    className="px-3 py-1 text-sm border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition"
                                >
                                    Load Template
                                </button>
                            )}
                        </div>

                        {/* Add Attribute Input */}
                        <div className="flex gap-2 mb-4">
                            <input
                                type="text"
                                value={newAttribute}
                                onChange={(e) => setNewAttribute(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAttribute())}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter attribute name (e.g., chassis, licensePlate)"
                            />
                            <button
                                type="button"
                                onClick={handleAddAttribute}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Add
                            </button>
                        </div>

                        {/* Attributes List */}
                        {attributes.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {attributes.map((attr, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg"
                                    >
                                        <span className="text-sm text-gray-900">{attr}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveAttribute(index)}
                                            className="text-red-500 hover:text-red-700 transition"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-gray-500 text-sm">
                                    No attributes added yet. Add attributes that will be required for this asset type.
                                </p>
                            </div>
                        )}

                        {/* Attribute Examples */}
                        {formData.assetType && (
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-900 font-medium mb-2">
                                    Suggested attributes for {formData.assetType}:
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {attributeTemplates[formData.assetType].map((attr, index) => (
                                        <span key={index} className="px-2 py-1 bg-white text-blue-700 text-xs rounded border border-blue-300">
                                            {attr}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex items-center gap-3 pt-6 border-t">
                        <button
                            type="submit"
                            disabled={saving || !formData.name || !formData.companyId || !formData.assetType || !formData.applicableClientType}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    {isEditMode ? 'Update Requirement' : 'Create Requirement'}
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/compliance/assets/requirements')}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AssetRequirementForm;
